import {
  type ItemSetCreationRequirementInput,
  OrderType,
  QBankStructureStatus,
  useCreateQuestionCreationTaskMutation,
  useSearchQBankStructuresLazyQuery,
  useSearchUsersByAppRoleFiltersLazyQuery,
} from '@exam-management/graphql';
import { QuestionCreationCountSummary } from '@exam-management/question-creation-task-details';
import { FormRichTextEditor, parseContentForApi } from '@exam-management/rich-text-editor';
import { Link, useNavigate } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Flex,
  Form,
  FormHelperText,
  FormDateInput,
  FormLabel,
  FormNumberInput,
  FormSearchSelection,
  FormTextField,
  PaperSection,
  PersonOutlinedIcon,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useUploadImageForQuestionCreationTask } from '../../hooks';

import {
  ITEM_SET_SIZES,
  type ItemSetQuestionFormOutput,
  type QuestionCreationTaskCreationFormInput,
  type QuestionCreationTaskCreationFormOutput,
  QuestionCreationTaskCreationFormSchema,
} from './schema';

export default function QuestionCreationTaskCreationFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [searchUser, { data: userListData, loading: isSearchingUserList }] =
    useSearchUsersByAppRoleFiltersLazyQuery();

  const [createQuestionCreationTask, { loading: isCreatingTask }] =
    useCreateQuestionCreationTaskMutation({
      onCompleted: (data) => {
        navigate('/task-management/question-creation/:taskId', {
          params: { taskId: data.wflCreateQuestionCreationTask.task.id },
        });
      },
    });

  const convertItemSetQuestionFormOutputToApiInput = (
    data: ItemSetQuestionFormOutput,
  ): ItemSetCreationRequirementInput[] => {
    return Object.entries(data)
      .map(([key, value]) => {
        if (value !== null) {
          return {
            size: parseInt(key),
            needTotal: value,
          };
        }
        return null;
      })
      .filter((item) => item !== null);
  };
  const handleCreateQuestionCreationTask = async (data: QuestionCreationTaskCreationFormOutput) => {
    await createQuestionCreationTask({
      variables: {
        createQuestionCreationTaskInput: {
          name: data.taskName,
          assigneeId: data.assignee.value,
          dueDate: data.dueDate.toISOString(),
          requirement: {
            qBankStructureId: data.requirement.qBankStructure.value,
            details: parseContentForApi(data.requirement.details ?? ''),
            requiredQuestionCount: data.requirement.questionCount.single ?? 0,
            itemSetCondition: convertItemSetQuestionFormOutputToApiInput(
              data.requirement.questionCount.itemSet,
            ),
          },
        },
      },
    });
  };

  const formContext = useForm<
    QuestionCreationTaskCreationFormInput,
    unknown,
    QuestionCreationTaskCreationFormOutput
  >({
    resolver: zodResolver(QuestionCreationTaskCreationFormSchema),
    defaultValues: {
      taskName: '',
      assignee: null,
      dueDate: null,
      requirement: {
        qBankStructure: null,
        details: '',
        questionCount: {
          single: null,
          itemSet: {
            '2': null,
            '3': null,
            '4': null,
            '5': null,
          },
        },
      },
    },
  });

  const handleSearchUser = (inputValue: string | null) => {
    const [firstname, lastname] = inputValue?.trim().split(' ') ?? [];
    searchUser({
      variables: {
        appRoleNames: [],
        order: {
          type: OrderType.Asc,
          field: 'firstname',
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        search: {
          firstname: firstname,
          lastname: lastname,
          email: '',
          status: '',
        },
      },
      fetchPolicy: 'network-only',
    });
  };

  const userOptions = useMemo(() => {
    if (isSearchingUserList || !userListData) return [];

    const { users } = userListData.UsBoSearchUsersWithAppRoleFilters;

    return users.map((user) => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    }));
  }, [isSearchingUserList, userListData]);

  const handleSubmit = async (data: QuestionCreationTaskCreationFormOutput) => {
    showPromiseSnackbar({
      message: {
        pending: 'Creating Task',
        success: 'Task created successfully',
        error: 'Failed to create task',
      },
      promise: () => handleCreateQuestionCreationTask(data),
    });
  };

  const [searchQBankStructure, { data: qBankStructureData, loading: isSearchingQBankStructure }] =
    useSearchQBankStructuresLazyQuery();

  const handleSearchQBankStructure = (inputValue: string | null) => {
    searchQBankStructure({
      variables: {
        order: {
          field: 'name',
          type: OrderType.Asc,
        },
        search: {
          name: inputValue ?? '',
          status: [QBankStructureStatus.Published, QBankStructureStatus.Unpublished],
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
      },
    });
  };

  const qBankStructureOptions = useMemo(() => {
    if (isSearchingQBankStructure || !qBankStructureData) return [];

    return qBankStructureData.qbsSearchQBankStructures.qBankStructures.map((structure) => ({
      label: structure.name,
      value: structure.id,
    }));
  }, [qBankStructureData, isSearchingQBankStructure]);

  const { handleUpload, loading: isUploadingImage } = useUploadImageForQuestionCreationTask({});

  const getShouldDisplayQuestionCountSummary = (
    singleCount: number | null,
    itemSetCount: ItemSetCreationRequirementInput[],
  ) => {
    if (singleCount !== null && singleCount > 0) return true;

    for (const { needTotal } of itemSetCount) {
      if (needTotal !== null && needTotal > 0) {
        return true;
      }
    }

    return false;
  };

  const singleQuestionCount = formContext.watch('requirement.questionCount.single') as
    | number
    | null;
  const itemSetQuestionCountFormValue = formContext.watch(
    'requirement.questionCount.itemSet',
  ) as ItemSetQuestionFormOutput;
  const itemSetQuestionCount = convertItemSetQuestionFormOutputToApiInput(
    itemSetQuestionCountFormValue,
  );
  const shouldDisplayQuestionCountSummary = getShouldDisplayQuestionCountSummary(
    singleQuestionCount,
    itemSetQuestionCount,
  );

  const questionCountError = formContext.formState.errors?.requirement?.questionCount?.root;

  const {
    watch,
    trigger,
    formState: { isSubmitted },
  } = formContext;

  useEffect(() => {
    // This is a workaround to trigger validation of related fields
    // when one of the question count fields changes after form submission.
    // since RHF doesn't support cross-field validation out of the box,
    // we need to manually trigger validation for related fields when one of them changes.
    const subscription = watch((_, { name }) => {
      if (name?.startsWith('requirement.questionCount') && isSubmitted) {
        trigger([
          'requirement.questionCount',
          'requirement.questionCount.single',
          ...ITEM_SET_SIZES.map((size) => `requirement.questionCount.itemSet.${size}` as const),
        ]);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, trigger, isSubmitted]);

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(handleSubmit)}
        sx={(theme) => ({ display: 'flex', gap: theme.spacing(6), flexDirection: 'column' })}
      >
        <PaperSection title='Task Information'>
          {/* Task Information form fields */}
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              p: theme.spacing(6),
              gap: theme.spacing(4),
            })}
          >
            <FormTextField
              name='taskName'
              label='Task Name'
              placeholder='กรุณากรอก Task Name'
              required
            />
            <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
              <FormSearchSelection
                name='assignee'
                label='Assignee'
                mode='fetch-debounce'
                options={userOptions}
                placeholder='กรุณาเลือก Assignee'
                loading={isSearchingUserList}
                required
                onSearch={handleSearchUser}
                sx={{ flex: 1 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <PersonOutlinedIcon
                        sx={(theme) => ({
                          fontSize: '20px',
                          mr: theme.spacing(2),
                          color: theme.palette.grey[500],
                        })}
                      />
                    ),
                  },
                }}
              />
              <Box sx={{ flex: 1 }}>
                <FormDateInput
                  name='dueDate'
                  label='Due Date'
                  placeholder='กรุณาเลือก Due Date'
                  inputFormat='DD-MM-YYYY'
                  required
                  minDate={new Date()}
                />
              </Box>
            </Flex>
          </Flex>
        </PaperSection>
        <PaperSection title='Question Information'>
          {/* Task Requirement form fields */}
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              p: theme.spacing(6),
              gap: theme.spacing(4),
            })}
          >
            <FormSearchSelection
              name='requirement.qBankStructure'
              options={qBankStructureOptions}
              label='เลือกคลังข้อสอบ'
              placeholder='เลือกคลังข้อสอบ'
              mode='fetch-debounce'
              loading={isSearchingQBankStructure}
              required
              onSearch={handleSearchQBankStructure}
            />
            <FormRichTextEditor
              name='requirement.details'
              label='รายละเอียดการออกข้อสอบ'
              placeholder='กรอกรายละเอียด'
              onImageUpload={handleUpload}
              loading={isUploadingImage}
              required
              rows={2}
            />
            <Flex sx={(theme) => ({ gap: theme.spacing(6), flexDirection: 'column' })}>
              <Flex sx={(theme) => ({ gap: theme.spacing(1), flexDirection: 'column' })}>
                <FormLabel required sx={(theme) => ({ ...theme.typography.body1Semibold })}>
                  ระบุจำนวนคำถามที่ต้องการ
                </FormLabel>
                <Text variant='caption'>กรอกจำนวนเฉพาะประเภทคำถามที่ต้องการ</Text>
              </Flex>
            </Flex>
            <FormNumberInput
              name='requirement.questionCount.single'
              label='Single'
              defaultValue={null}
              placeholder='ระบุจำนวนข้อ'
              min={1}
              sx={{ maxWidth: '200px' }}
            />

            <Flex sx={(theme) => ({ gap: theme.spacing(2), flexDirection: 'column' })}>
              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                {ITEM_SET_SIZES.map((size) => (
                  <FormNumberInput
                    defaultValue={null}
                    key={`itemSet-${size}`}
                    name={`requirement.questionCount.itemSet.${size}` as const}
                    label={`Item Set : ${size}Q`}
                    placeholder='ระบุจำนวน Set'
                    min={1}
                    sx={{ maxWidth: '200px' }}
                  />
                ))}
              </Flex>
              {questionCountError ? (
                <FormHelperText error={!!questionCountError}>
                  {questionCountError.message}
                </FormHelperText>
              ) : null}
            </Flex>
            {shouldDisplayQuestionCountSummary ? (
              <QuestionCreationCountSummary
                singleQuestionCount={singleQuestionCount ?? 0}
                itemSetQuestion={itemSetQuestionCount.map(({ size, needTotal }) => ({
                  questionInItemSet: size,
                  itemSetCount: needTotal,
                }))}
              />
            ) : null}
          </Flex>
        </PaperSection>
        <Flex sx={(theme) => ({ justifyContent: 'flex-end', gap: theme.spacing(2) })}>
          <Button
            variant='outlined'
            color='secondary'
            component={Link}
            to='/task-management/question-creation'
          >
            Cancel
          </Button>
          <Button loading={isCreatingTask} type='submit'>
            Create Task
          </Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
